import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCms } from '../contexts/CmsContext';
import { 
  FaHome, 
  FaLayerGroup, 
  FaEnvelope, 
  FaSignOutAlt, 
  FaSave, 
  FaEye, 
  FaImage,
  FaCheckCircle,
  FaGlobe,
  FaColumns,
  FaInfoCircle,
  FaBriefcase
} from 'react-icons/fa';

// Helper Components moved outside to prevent re-rendering issues
const SidebarItem = ({ id, icon: Icon, label, activeTab, setActiveTab }) => (
  <button
    type="button"
    onClick={() => setActiveTab(id)}
    className={`w-full flex items-center space-x-3 px-6 py-4 transition-colors ${
      activeTab === id 
        ? 'bg-blue-600 text-white' 
        : 'text-gray-400 hover:bg-slate-800 hover:text-white'
    }`}
  >
    <Icon size={20} />
    <span className="font-medium">{label}</span>
  </button>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
    <h3 className="text-lg font-bold text-gray-800 mb-6 pb-2 border-b border-gray-100">{title}</h3>
    <div className="space-y-6">
      {children}
    </div>
  </div>
);

const InputField = ({ label, path, placeholder, getValue, updateField }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-600 mb-2">{label}</label>
    <input
      type="text"
      value={getValue(path)}
      onChange={(e) => updateField(path, e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-700"
    />
  </div>
);

const TextAreaField = ({ label, path, rows = 3, getValue, updateField }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-600 mb-2">{label}</label>
    <textarea
      rows={rows}
      value={getValue(path)}
      onChange={(e) => updateField(path, e.target.value)}
      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-700"
    />
  </div>
);

const ImageField = ({ label, path, getValue, updateField, lang }) => {
  const url = getValue(path);
  const [uploading, setUploading] = useState(false);
  const isRTL = lang === 'ar';

  const t = {
    upload: isRTL ? 'رفع صورة' : 'Upload Image',
    uploading: isRTL ? 'جاري الرفع...' : 'Uploading...',
    placeholder: isRTL ? 'رابط الصورة' : 'Image URL',
    error: isRTL ? 'فشل الرفع' : 'Upload failed',
    genericError: isRTL ? 'خطأ في الرفع' : 'Error uploading image'
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setUploading(true);
    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid server response: ' + text.substring(0, 100));
      }

      if (data.success) {
        updateField(path, data.url);
      } else {
        alert(t.error + ': ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(t.genericError);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-600 mb-2">{label}</label>
      <div className="flex gap-4 items-start">
        <div className="flex-grow space-y-3">
          <div className="relative">
            <FaImage className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 text-gray-400`} />
            <input
              type="text"
              value={url}
              onChange={(e) => updateField(path, e.target.value)}
              placeholder={t.placeholder}
              className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-700 font-mono text-sm`}
              dir="ltr"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <div className="relative w-full">
               <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className={`flex items-center justify-center gap-2 w-full py-2 px-4 border border-dashed border-blue-300 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors ${uploading ? 'opacity-50' : ''}`}>
                  <FaImage />
                  <span className="text-sm font-semibold">{uploading ? t.uploading : t.upload}</span>
              </div>
            </div>
          </div>
        </div>
        
        {url && (
          <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0">
            <img src={url} alt="Preview" className="w-full h-full object-cover" />
          </div>
        )}
      </div>
    </div>
  );
};

const ListField = ({ label, path, getValue, updateField }) => {
  const items = getValue(path) || [];
  const textValue = Array.isArray(items) ? items.join('\n') : '';

  const handleChange = (e) => {
    const newItems = e.target.value.split('\n').filter(item => item.trim() !== '');
    updateField(path, newItems);
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-600 mb-2">{label} (One per line)</label>
      <textarea
        rows={5}
        value={textValue}
        onChange={handleChange}
        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-700"
        placeholder="Item 1&#10;Item 2&#10;Item 3"
      />
    </div>
  );
};

const DetailedItemEditor = ({ item, index, onChange, onDelete, lang }) => {
  const [uploading, setUploading] = useState(false);
  const isRTL = lang === 'ar';
  
  const t = {
    upload: isRTL ? 'رفع' : 'Upload Image',
    uploading: isRTL ? '...' : 'Uploading...',
    addUrl: isRTL ? '+ رابط صورة' : '+ Add URL',
    productTitle: isRTL ? 'عنوان المنتج' : 'Product Title',
    images: isRTL ? 'صور (روابط)' : 'Images (URLs)',
    specs: isRTL ? 'المواصفات' : 'Specifications',
    addSpec: isRTL ? '+ إضافة مواصفة' : '+ Add Spec',
    footer: isRTL ? 'ملاحظة تذييل' : 'Footer Note',
    placeholderTitle: isRTL ? 'عنوان المنتج' : 'Product Title',
    placeholderImg: isRTL ? 'رابط الصورة' : 'Image URL',
    placeholderSpecVal: isRTL ? 'القيمة (سطر لكل عنصر)' : 'Value (one per line for list)',
    placeholderFooter: isRTL ? 'مثال: مقاسات خاصة عند الطلب' : 'e.g. Special sizes available on request'
  };
  const handleSpecKeyChange = (oldKey, newKey) => {
    if (!newKey) return;
    const { [oldKey]: value, ...rest } = item.specs || {};
    const newSpecs = { ...rest, [newKey]: value };
    onChange(index, 'specs', newSpecs);
  };

  const handleSpecValueChange = (key, value) => {
    // Check if value contains newlines -> convert to array
    const isArray = value.includes('\n');
    const finalValue = isArray ? value.split('\n').filter(s => s.trim()) : value;
    const newSpecs = { ...(item.specs || {}), [key]: finalValue };
    onChange(index, 'specs', newSpecs);
  };

  const addSpec = () => {
    const newSpecs = { ...(item.specs || {}), 'New Spec': 'Value' };
    onChange(index, 'specs', newSpecs);
  };

  const removeSpec = (key) => {
    const { [key]: _, ...newSpecs } = item.specs || {};
    onChange(index, 'specs', newSpecs);
  };
  
  const handleImageChange = (imgIndex, val) => {
      const newImages = [...(item.images || [])];
      newImages[imgIndex] = val;
      onChange(index, 'images', newImages);
  };

  const addImage = () => {
      onChange(index, 'images', [...(item.images || []), '']);
  };
  
  const removeImage = (imgIndex) => {
      onChange(index, 'images', (item.images || []).filter((_, i) => i !== imgIndex));
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setUploading(true);
    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid server response: ' + text.substring(0, 100));
      }

      if (data.success) {
        // Append uploaded image to the list
        const newImages = [...(item.images || []), data.url];
        onChange(index, 'images', newImages);
      } else {
        alert((isRTL ? 'فشل الرفع' : 'Upload failed') + ': ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(isRTL ? 'خطأ في الرفع' : 'Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-200 relative group">
        <button type="button" onClick={() => onDelete(index)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 bg-white rounded-full p-1 shadow-sm" title="Remove Item"><FaSignOutAlt className="transform rotate-180" size={12} /></button>
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 uppercase">{t.productTitle}</span>
                <input 
                    type="text" 
                    value={item.name} 
                    onChange={(e) => onChange(index, 'name', e.target.value)}
                    className="flex-grow font-bold bg-transparent border-b border-blue-200 focus:border-blue-500 outline-none px-1 text-gray-800"
                    placeholder={t.placeholderTitle}
                />
            </div>

            {/* Images */}
            <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t.images}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(item.images || []).map((img, i) => (
                        <div key={i} className="flex gap-1 items-center">
                            <div className="relative flex-grow">
                                <input 
                                    type="text" 
                                    value={img} 
                                    onChange={(e) => handleImageChange(i, e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:border-blue-500 outline-none pr-8"
                                    placeholder={t.placeholderImg}
                                    dir="ltr"
                                />
                                {img && (
                                  <div className={`absolute ${isRTL ? 'left-1' : 'right-1'} top-1 w-4 h-4 rounded overflow-hidden border border-gray-200`}>
                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                  </div>
                                )}
                            </div>
                            <button type="button" onClick={() => removeImage(i)} className="text-red-400 hover:text-red-600 px-1">×</button>
                        </div>
                    ))}
                </div>
                <div className="flex gap-3 mt-2 items-center">
                    <button type="button" onClick={addImage} className="text-xs text-blue-600 hover:underline">{t.addUrl}</button>
                    <div className="h-4 w-px bg-gray-300"></div>
                    <label className={`cursor-pointer text-xs bg-white border border-blue-200 text-blue-600 px-2 py-1 rounded hover:bg-blue-50 transition-colors flex items-center gap-1 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <FaImage size={10} />
                        <span>{uploading ? t.uploading : t.upload}</span>
                        <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={handleFileUpload}
                            disabled={uploading} 
                        />
                    </label>
                </div>
            </div>

            {/* Specs */}
            <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t.specs}</label>
                <div className="space-y-2">
                    {Object.entries(item.specs || {}).map(([key, value], i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-start">
                            <input 
                                type="text" 
                                value={key} 
                                onChange={(e) => handleSpecKeyChange(key, e.target.value)}
                                className="col-span-4 px-2 py-1 text-xs font-medium border border-gray-200 rounded bg-gray-50 focus:border-blue-500 outline-none"
                            />
                            <textarea 
                                value={Array.isArray(value) ? value.join('\n') : value}
                                onChange={(e) => handleSpecValueChange(key, e.target.value)}
                                className="col-span-7 px-2 py-1 text-xs border border-gray-200 rounded resize-none focus:border-blue-500 outline-none"
                                rows={Array.isArray(value) ? Math.min(value.length + 1, 5) : 1}
                                placeholder={t.placeholderSpecVal}
                            />
                            <button type="button" onClick={() => removeSpec(key)} className="col-span-1 text-red-400 hover:text-red-600 flex justify-center mt-1">×</button>
                        </div>
                    ))}
                    <button type="button" onClick={addSpec} className="text-xs text-blue-600 hover:underline">{t.addSpec}</button>
                </div>
            </div>

            {/* Footer Note */}
            <div>
                 <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t.footer}</label>
                 <input 
                    type="text" 
                    value={item.footerNote || ''} 
                    onChange={(e) => onChange(index, 'footerNote', e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:border-blue-500 outline-none"
                    placeholder={t.placeholderFooter}
                 />
            </div>
        </div>
    </div>
  );
};

const ProductSubItemsField = ({ label, path, getValue, updateField, lang }) => {
  const items = getValue(path) || [];
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const isRTL = lang === 'ar';
  
  const t = {
    addSubCard: isRTL ? '+ بطاقة فرعية' : '+ Sub Card',
    addProductTitle: isRTL ? '+ عنوان منتج' : '+ Product Title',
    image: isRTL ? 'صورة' : 'Image',
    name: isRTL ? 'الاسم' : 'Name',
    desc: isRTL ? 'الوصف' : 'Description',
    placeholderImg: isRTL ? 'رابط الصورة' : 'Image URL',
    placeholderName: isRTL ? 'اسم العنصر' : 'Item Name',
    placeholderDesc: isRTL ? 'وصف العنصر' : 'Item Description',
    noItems: isRTL ? 'لم تتم إضافة عناصر بعد. انقر فوق "+ بطاقة فرعية" للبدء.' : 'No items added yet. Click "+ Sub Card" to start.'
  };

  // Ensure items is an array
  const safeItems = Array.isArray(items) ? items : [];

  const handleItemChange = (index, field, value) => {
    const newItems = [...safeItems];
    // Handle string items by converting to object on edit
    if (typeof newItems[index] === 'string') {
      newItems[index] = { name: newItems[index], image: '', description: '' };
    }
    newItems[index] = { ...newItems[index], [field]: value };
    updateField(path, newItems);
  };

  const handleDelete = (index) => {
    const newItems = safeItems.filter((_, i) => i !== index);
    updateField(path, newItems);
  };

  const handleAdd = () => {
    // Add simple item by default
    const newItems = [...safeItems, { name: '', image: '', description: '' }];
    updateField(path, newItems);
  };
  
  const handleAddDetailed = () => {
      const newItems = [...safeItems, { 
          name: '', 
          type: 'detailed', 
          images: [], 
          specs: { 'Feature': 'Value' }, 
          footerNote: '' 
      }];
      updateField(path, newItems);
  };

  const handleFileUpload = async (e, index) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setUploadingIndex(index);
    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (data.success) {
        handleItemChange(index, 'image', data.url);
      } else {
        alert('Upload failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploadingIndex(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <label className="block text-sm font-semibold text-gray-600">{label}</label>
        <div className="space-x-2">
            <button
              type="button"
              onClick={handleAdd}
              className="text-xs bg-gray-50 text-gray-600 px-3 py-1 rounded-md hover:bg-gray-100 font-medium transition-colors"
            >
              {t.addSubCard}
            </button>
            <button
              type="button"
              onClick={handleAddDetailed}
              className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-md hover:bg-blue-100 font-medium transition-colors"
            >
              {t.addProductTitle}
            </button>
        </div>
      </div>
      <div className="space-y-4">
        {safeItems.map((item, index) => {
          const isObject = typeof item === 'object' && item !== null;
          const isDetailed = isObject && item.type === 'detailed';

          if (isDetailed) {
              return <DetailedItemEditor key={index} item={item} index={index} onChange={handleItemChange} onDelete={handleDelete} lang={lang} />;
          }

          const name = isObject ? item.name : item;
          const description = isObject ? item.description : '';
          const image = isObject ? item.image : '';

          return (
            <div key={index} className="bg-gray-50 p-4 rounded-xl border border-gray-200 relative group">
              <button
                type="button"
                onClick={() => handleDelete(index)}
                className="absolute top-2 right-2 text-gray-400 hover:text-red-500 bg-white rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-all"
                title="Remove Item"
              >
                <FaSignOutAlt className="transform rotate-180" size={12} />
              </button>
              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* Image Upload - Simplified for SubItem */}
                <div className="md:col-span-3">
                   <div className="space-y-2">
                     <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t.image}</label>
                     <div className="h-24 w-full bg-white border border-gray-200 rounded-lg overflow-hidden relative">
                        {image ? (
                          <img src={image} alt={name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <FaImage size={24} />
                          </div>
                        )}
                        <input
                          type="text"
                          value={image}
                          onChange={(e) => handleItemChange(index, 'image', e.target.value)}
                          className="absolute bottom-0 w-full text-[10px] px-2 py-1 bg-white/90 border-t border-gray-100 outline-none"
                          placeholder={t.placeholderImg}
                          dir="ltr"
                        />
                     </div>
                     <div className="flex items-center gap-2">
                        <div className="relative w-full">
                           <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, index)}
                            disabled={uploadingIndex === index}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          />
                          <div className="flex items-center justify-center w-full py-1 bg-blue-50 text-blue-600 rounded text-[10px] font-semibold hover:bg-blue-100 transition-colors">
                              {uploadingIndex === index ? '...' : <FaImage size={10} />}
                          </div>
                        </div>
                      </div>
                   </div>
                </div>

                {/* Content Fields */}
                <div className="md:col-span-9 space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t.name}</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder={t.placeholderName}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{t.desc}</label>
                    <textarea
                      rows={2}
                      value={description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                      placeholder={t.placeholderDesc}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {safeItems.length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-gray-400 text-sm">
            {t.noItems}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { isAuthenticated, logout } = useAuth();
  const { getContent, updateContent, loading: cmsLoading } = useCms();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('home');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [content, setContent] = useState(null);
  const [status, setStatus] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Load content
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!cmsLoading) {
      setContent(getContent(selectedLanguage));
    }
  }, [isAuthenticated, cmsLoading, getContent, navigate, selectedLanguage]);

  const handleSave = async () => {
    setIsSaving(true);
    setStatus('');
    try {
      await updateContent({ [selectedLanguage]: content });
      setStatus('success');
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      console.error(err);
      setStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (path, value) => {
    // Helper to update nested state safely
    setContent(prev => {
      const newState = { ...prev };
      let current = newState;
      const parts = path.split('.');
      const last = parts.pop();
      
      parts.forEach(part => {
        if (!current[part]) current[part] = {};
        current = current[part];
      });
      
      current[last] = value;
      return newState;
    });
  };

  // Helper to get value safely
  const getValue = (path) => {
    if (!content) return '';
    return path.split('.').reduce((obj, key) => obj?.[key], content) || '';
  };

  if (!content || cmsLoading) return <div className="flex h-screen items-center justify-center" dir="ltr">Loading...</div>;

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans" dir="ltr">
      {/* Sidebar */}
      <aside className="w-72 bg-slate-900 flex flex-col fixed h-full z-10">
        <div className="p-8">
          <h1 className="text-2xl font-bold text-white tracking-tight">AMHBM CMS</h1>
          <p className="text-slate-500 text-xs font-bold tracking-widest mt-2 uppercase">Administrator</p>
        </div>
        <nav className="flex-grow py-4">
          <SidebarItem id="home" icon={FaHome} label="Home Page" activeTab={activeTab} setActiveTab={setActiveTab} />
          <SidebarItem id="about" icon={FaInfoCircle} label="About Page" activeTab={activeTab} setActiveTab={setActiveTab} />
          <SidebarItem id="services" icon={FaLayerGroup} label="Products" activeTab={activeTab} setActiveTab={setActiveTab} />
          <SidebarItem id="career" icon={FaBriefcase} label="Career Page" activeTab={activeTab} setActiveTab={setActiveTab} />
          <SidebarItem id="contact" icon={FaEnvelope} label="Contact Page" activeTab={activeTab} setActiveTab={setActiveTab} />
          <SidebarItem id="footer" icon={FaColumns} label="Footer" activeTab={activeTab} setActiveTab={setActiveTab} />
        </nav>
        <div className="p-6 border-t border-slate-800">
          <button 
            onClick={() => { logout(); navigate('/login'); }}
            className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors w-full"
          >
            <FaSignOutAlt />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-grow ml-72">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20 px-8 py-4 flex justify-between items-center shadow-sm">
          <h2 className="text-xl font-bold text-gray-800">
            {activeTab === 'home' && 'Home Page Management'}
            {activeTab === 'about' && 'About Page Management'}
            {activeTab === 'services' && 'Products Management'}
            {activeTab === 'career' && 'Career Page Management'}
            {activeTab === 'contact' && 'Contact Page Management'}
            {activeTab === 'footer' && 'Footer Management'}
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setSelectedLanguage('en')}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${
                  selectedLanguage === 'en'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                English
              </button>
              <button
                onClick={() => setSelectedLanguage('ar')}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${
                  selectedLanguage === 'ar'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                العربية
              </button>
            </div>
            {status === 'success' && (
              <span className="text-green-600 font-medium flex items-center gap-2 animate-pulse">
                <FaCheckCircle /> Saved Successfully
              </span>
            )}
            <button 
                onClick={() => window.open('/', '_blank')}
                className="text-gray-500 hover:text-blue-600 font-medium flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-all"
            >
                <FaEye /> Preview Site
            </button>
            <button 
              onClick={handleSave}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <FaSave /> {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="p-8 max-w-5xl mx-auto pb-24">
          {activeTab === 'home' && (
            <>
              {/* Hero Section */}
              <SectionCard title="Hero Section">
                <div className="grid grid-cols-2 gap-6">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Top Badge" path="home.hero.badge" />
                  <div className="grid grid-cols-2 gap-2">
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Primary CTA" path="home.hero.explore" />
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Secondary CTA" path="home.hero.requestQuote" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 1" path="home.hero.titleLine1" />
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Emphasis Word" path="home.hero.titleEmphasis" />
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 2" path="home.hero.titleLine2" />
                </div>
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Subtitle" path="home.hero.subtitle" />
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Hero Background Image" path="home.hero.bgImage" />
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Vision 2030 Logo" path="home.hero.visionLogo" />
              </SectionCard>

              {/* Badges Section */}
              <SectionCard title="Feature Badges">
                <div className="grid grid-cols-2 gap-6">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 1" path="home.badges.delivery" />
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 2" path="home.badges.certified" />
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 3" path="home.badges.iso" />
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 4" path="home.badges.support" />
                </div>
              </SectionCard>

              {/* About Section */}
              <SectionCard title="About Summary">
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Label" path="home.about.label" />
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Main Title" path="home.about.title" />
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="home.about.description" rows={5} />
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="About Section Image" path="home.about.image" />
                <div className="grid grid-cols-2 gap-6 mt-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Vision Title" path="home.about.vision.title" />
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Vision Text" path="home.about.vision.text" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Mission Title" path="home.about.mission.title" />
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Mission Text" path="home.about.mission.text" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Client Count (Legacy Years)" path="home.about.legacyYears" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Client Text (Legacy Text)" path="home.about.legacyText" />
                </div>
              </SectionCard>

              {/* Solutions Section */}
              <SectionCard title="Solutions & Products">
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="home.solutions.title" />
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Description" path="home.solutions.description" />
                
                <div className="mt-8 border-t border-gray-100 pt-6">
                    <h4 className="font-bold text-gray-700 mb-4">Category: Pipes & Tubing</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="home.solutions.categories.pipes.title" />
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge" path="home.solutions.categories.pipes.badge" />
                    </div>
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="home.solutions.categories.pipes.desc" />
                    <div className="mt-4">
                        <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="home.solutions.categories.pipes.image" />
                    </div>
                </div>

                <div className="mt-8 border-t border-gray-100 pt-6">
                    <h4 className="font-bold text-gray-700 mb-4">Category: Valves </h4>
                     <div className="grid grid-cols-2 gap-4 mb-4">
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="home.solutions.categories.valves.title" />
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge" path="home.solutions.categories.valves.badge" />
                    </div>
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="home.solutions.categories.valves.desc" />
                    <div className="mt-4">
                        <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="home.solutions.categories.valves.image" />
                    </div>
                </div>

                <div className="mt-8 border-t border-gray-100 pt-6">
                    <h4 className="font-bold text-gray-700 mb-4">Category: Electrical</h4>
                     <div className="grid grid-cols-2 gap-4 mb-4">
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="home.solutions.categories.electrical.title" />
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge" path="home.solutions.categories.electrical.badge" />
                    </div>
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="home.solutions.categories.electrical.desc" />
                    <div className="mt-4">
                        <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="home.solutions.categories.electrical.image" />
                    </div>
                </div>

                <div className="mt-8 border-t border-gray-100 pt-6">
                    <h4 className="font-bold text-gray-700 mb-4">Value-Added Services</h4>
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Services Title" path="home.solutions.services.title" />
                    <div className="grid grid-cols-2 gap-6 mt-4">
                        <div>
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Procurement Title" path="home.solutions.services.procurement.title" />
                            <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Procurement Text" path="home.solutions.services.procurement.text" />
                        </div>
                        <div>
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Logistics Title" path="home.solutions.services.logistics.title" />
                            <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Logistics Text" path="home.solutions.services.logistics.text" />
                        </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4 mt-4">
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Industry 1" path="home.solutions.services.industries.oil" />
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Industry 2" path="home.solutions.services.industries.industry" />
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Industry 3" path="home.solutions.services.industries.energy" />
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Industry 4" path="home.solutions.services.industries.civil" />
                    </div>
                </div>
              </SectionCard>

               {/* Commitment Section */}
               <SectionCard title="Our Commitment">
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="home.commitment.title" />
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="home.commitment.description" />
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Commitment Image" path="home.commitment.image" />
                <div className="grid grid-cols-4 gap-4 mt-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Standard 1 (ASME)" path="home.commitment.standards.asme" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Standard 2 (ASTM)" path="home.commitment.standards.astm" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Standard 3 (ISO)" path="home.commitment.standards.iso" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Standard 4 (DIN)" path="home.commitment.standards.din" />
                </div>
              </SectionCard>

              {/* Vision 2030 Section */}
              <SectionCard title="Vision 2030">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="home.vision2030.title" />
                  <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="home.vision2030.text" />
                  <div className="grid grid-cols-2 gap-6 mt-4">
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Partner Label" path="home.vision2030.partner" />
                      <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Logo" path="home.vision2030.logo" />
                  </div>
              </SectionCard>

              {/* Home Contact Section */}
              <SectionCard title="Home Contact CTA">
                  <div className="grid grid-cols-2 gap-6">
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 1" path="home.contact.titleLine1" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Emphasis" path="home.contact.titleEmphasis" />
                  </div>
                  <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Subtitle" path="home.contact.subtitle" />
                  <div className="grid grid-cols-2 gap-6 mt-4">
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="CTA Button 1" path="home.contact.cta1" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="CTA Button 2" path="home.contact.cta2" />
                  </div>
              </SectionCard>
            </>
          )}

          {activeTab === 'about' && (
            <>
              {/* Hero Section */}
              <SectionCard title="Hero Section">
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Hero Background Image" path="aboutPage.hero.bgImage" />
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge" path="aboutPage.hero.badge" />
                <div className="grid grid-cols-2 gap-6 mt-4">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 1" path="aboutPage.hero.titleLine1" />
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Emphasis Word" path="aboutPage.hero.titleEmphasis" />
                </div>
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Subtitle" path="aboutPage.hero.subtitle" />
              </SectionCard>

              {/* Pillars Section */}
              <SectionCard title="Core Pillars">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Integrity</h4>
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Label" path="aboutPage.pillars.integrity.label" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="aboutPage.pillars.integrity.title" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Quality</h4>
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Label" path="aboutPage.pillars.quality.label" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="aboutPage.pillars.quality.title" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Commitment</h4>
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Label" path="aboutPage.pillars.commitment.label" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="aboutPage.pillars.commitment.title" />
                  </div>
                </div>
              </SectionCard>

              {/* Vision & Mission */}
              <SectionCard title="Vision & Mission">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                     <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Vision</h4>
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="aboutPage.visionMission.vision.title" />
                     <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="aboutPage.visionMission.vision.text" rows={5} />
                  </div>
                  <div>
                     <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Mission</h4>
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="aboutPage.visionMission.mission.title" />
                     <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="aboutPage.visionMission.mission.text" rows={5} />
                  </div>
                </div>
              </SectionCard>

              {/* CEO Quote */}
              <SectionCard title="Leadership Message">
                <div className="grid grid-cols-2 gap-6 mb-4">
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Overlay Title" path="aboutPage.quote.imageOverlayTitle" />
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Overlay Subtitle" path="aboutPage.quote.imageOverlaySubtitle" />
                </div>
                <div className="grid grid-cols-2 gap-6">
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Main Text" path="aboutPage.quote.text1" />
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Emphasis Text" path="aboutPage.quote.textEmphasis" />
                </div>
                <div className="mt-4 space-y-4">
                   <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Paragraph 1" path="aboutPage.quote.desc1" rows={4} />
                   <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Paragraph 2" path="aboutPage.quote.desc2" rows={4} />
                </div>
                <div className="grid grid-cols-2 gap-6 mt-4">
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Role" path="aboutPage.quote.role" />
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Company Name" path="aboutPage.quote.company" />
                </div>
                <div className="mt-4">
                   <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image URL" path="aboutPage.quote.image" />
                </div>
              </SectionCard>

              {/* Strategic Advantage */}
              <SectionCard title="Strategic Advantage">
                <div className="grid grid-cols-2 gap-6 mb-6">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Label" path="aboutPage.advantage.label" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Main Title" path="aboutPage.advantage.title" />
                </div>
                
                <div className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-bold text-gray-700 mb-2">Global Partnerships</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="aboutPage.advantage.global.title" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="aboutPage.advantage.global.text" />
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-bold text-gray-700 mb-2">Logistical Excellence</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="aboutPage.advantage.logistics.title" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="aboutPage.advantage.logistics.text" />
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-bold text-gray-700 mb-2">Technical Expertise</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="aboutPage.advantage.technical.title" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="aboutPage.advantage.technical.text" />
                    </div>
                </div>
              </SectionCard>

              {/* Vision 2030 */}
              <SectionCard title="Vision 2030 Alignment">
                  <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Logo URL" path="aboutPage.vision2030.logo" />
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Label" path="aboutPage.vision2030.label" />
                  <div className="grid grid-cols-2 gap-6 mt-4">
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 1" path="aboutPage.vision2030.titleLine1" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Emphasis" path="aboutPage.vision2030.titleEmphasis" />
                  </div>
                  <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="aboutPage.vision2030.text" rows={4} />
              </SectionCard>
            </>
          )}

          {activeTab === 'services' && (
            <>
              {/* Hero Section */}
              <SectionCard title="Hero Section">
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Hero Background Image" path="servicePage.hero.bgImage" />
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge" path="servicePage.hero.badge" />
                <div className="grid grid-cols-2 gap-6 mt-4">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 1" path="servicePage.hero.titleLine1" />
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Emphasis Word" path="servicePage.hero.titleEmphasis" />
                </div>
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Subtitle" path="servicePage.hero.subtitle" />
              </SectionCard>

              {/* Overview Section */}
              <SectionCard title="Catalog Overview">
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.overview.title" />
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.overview.description" />
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Catalog PDF URL" path="servicePage.catalogNav.catalogPdf" />
                </div>
              </SectionCard>

              {/* Sidebar Configuration */}
              <SectionCard title="Sidebar Configuration">
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Sidebar Title" path="servicePage.sidebar.title" />
                
                <div className="mt-4 border-t border-gray-100 pt-4">
                    <h5 className="font-bold text-gray-700 mb-2">Material Type Filter</h5>
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="servicePage.sidebar.materialType.title" />
                    <ListField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Options" path="servicePage.sidebar.materialType.options" />
                </div>

                <div className="mt-4 border-t border-gray-100 pt-4">
                    <h5 className="font-bold text-gray-700 mb-2">Standards Filter</h5>
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="servicePage.sidebar.standards.title" />
                    <ListField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Options" path="servicePage.sidebar.standards.options" />
                </div>

                <div className="mt-4 border-t border-gray-100 pt-4">
                    <h5 className="font-bold text-gray-700 mb-2">Help Section</h5>
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="servicePage.sidebar.help.text" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Link Text" path="servicePage.sidebar.help.link" />
                </div>
              </SectionCard>

              {/* Category: Pipes */}
              <SectionCard title="1. Pipes">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.pipes" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.pipes.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.pipes.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.pipes.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.pipes.items" />
                </div>
                <div className="grid grid-cols-2 gap-6 mt-4">
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 1 (Aramco)" path="servicePage.badges.aramcoApproved" />
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 2 (SABIC)" path="servicePage.badges.sabicReady" />
                </div>
              </SectionCard>

              {/* Category: Fittings */}
              <SectionCard title="2. Fittings">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.fittings" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.fittings.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.fittings.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.fittings.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.fittings.items" />
                </div>
              </SectionCard>

              {/* Category: Flanges */}
              <SectionCard title="3. Flanges">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.flanges" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.flanges.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.flanges.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.flanges.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.flanges.items" />
                </div>
              </SectionCard>

              {/* Category: Valves */}
              <SectionCard title="4. Valves">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.valves" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.valves.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.valves.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.valves.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.valves.items" />
                </div>
                <div className="grid grid-cols-2 gap-6 mt-4">
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 1 (API 6D)" path="servicePage.badges.api6d" />
                </div>
              </SectionCard>

              {/* Category: Fire Fighting */}
              <SectionCard title="5. Fire Fighting">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.fire" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.fire.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.fire.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.fire.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.fire.items" />
                </div>
                <div className="grid grid-cols-2 gap-6 mt-4">
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 1 (UL Listed)" path="servicePage.badges.ulListed" />
                     <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge 2 (FM Approved)" path="servicePage.badges.fmApproved" />
                </div>
              </SectionCard>

              {/* Category: Instrumentation */}
              <SectionCard title="6. Instrumentation">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.instrumentation" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.instrumentation.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.instrumentation.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.instrumentation.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.instrumentation.items" />
                </div>
              </SectionCard>

              {/* Category: Fasteners */}
              <SectionCard title="7. Fasteners">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.fasteners" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.fasteners.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.fasteners.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.fasteners.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.fasteners.items" />
                </div>
              </SectionCard>

              {/* Category: Gaskets */}
              <SectionCard title="8. Gaskets">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.gaskets" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.gaskets.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.gaskets.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.gaskets.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.gaskets.items" />
                </div>
              </SectionCard>

              {/* Category: Electrical */}
              <SectionCard title="9. Electrical">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.electrical" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.electrical.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.electrical.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.electrical.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.electrical.items" />
                </div>
              </SectionCard>

              {/* Category: Telecom */}
              <SectionCard title="10. Telecom">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.telecom" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.telecom.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.telecom.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.telecom.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.telecom.items" />
                </div>
              </SectionCard>

              {/* Category: Sanitary */}
              <SectionCard title="11. Sanitary">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.sanitary" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.sanitary.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.sanitary.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.sanitary.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.sanitary.items" />
                </div>
              </SectionCard>

              {/* Category: Chemicals */}
              <SectionCard title="12. Chemicals">
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Nav Title" path="servicePage.catalogNav.items.chemicals" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Section Title" path="servicePage.categories.chemicals.title" />
                </div>
                <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image" path="servicePage.categories.chemicals.image" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="servicePage.categories.chemicals.description" />
                </div>
                <div className="mt-4">
                    <ProductSubItemsField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Items" path="servicePage.categories.chemicals.items" />
                </div>
              </SectionCard>


            </>
          )}

          {activeTab === 'career' && (
            <>
              {/* Hero Section */}
              <SectionCard title="Hero Section">
                <div className="grid grid-cols-2 gap-6">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge" path="careerPage.hero.badge" />
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="CTA Button" path="careerPage.hero.cta" />
                </div>
                <div className="mt-4">
                  <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Background Image URL" path="careerPage.hero.image" />
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 1" path="careerPage.hero.titleLine1" />
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Emphasis Word" path="careerPage.hero.titleEmphasis" />
                   <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 2" path="careerPage.hero.titleLine2" />
                </div>
                <div className="mt-4">
                  <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Subtitle" path="careerPage.hero.subtitle" />
                </div>
              </SectionCard>

              {/* Culture Section */}
              <SectionCard title="Culture Section">
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge" path="careerPage.culture.badge" />
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.culture.title" />
                <div className="mt-4">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Description" path="careerPage.culture.description" />
                </div>
                
                <h4 className="font-bold text-gray-700 mt-6 mb-4 border-b pb-2">Culture Cards</h4>
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <h5 className="font-semibold text-sm text-gray-600 mb-2">Collaboration</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.culture.cards.collaboration.title" />
                        <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image URL" path="careerPage.culture.cards.collaboration.image" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.culture.cards.collaboration.text" rows={2} />
                    </div>
                    <div>
                        <h5 className="font-semibold text-sm text-gray-600 mb-2">Workshops</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.culture.cards.workshops.title" />
                        <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image URL" path="careerPage.culture.cards.workshops.image" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.culture.cards.workshops.text" rows={2} />
                    </div>
                    <div>
                        <h5 className="font-semibold text-sm text-gray-600 mb-2">Impact</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.culture.cards.impact.title" />
                        <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image URL" path="careerPage.culture.cards.impact.image" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.culture.cards.impact.text" rows={2} />
                    </div>
                    <div>
                        <h5 className="font-semibold text-sm text-gray-600 mb-2">Vision</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.culture.cards.vision.title" />
                        <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Image URL" path="careerPage.culture.cards.vision.image" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.culture.cards.vision.text" rows={2} />
                    </div>
                </div>
              </SectionCard>

              {/* Benefits Section */}
              <SectionCard title="Benefits Section">
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <h5 className="font-semibold text-sm text-gray-600 mb-2">Compensation</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.benefits.items.compensation.title" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.benefits.items.compensation.text" rows={2} />
                    </div>
                    <div>
                        <h5 className="font-semibold text-sm text-gray-600 mb-2">Growth</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.benefits.items.growth.title" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.benefits.items.growth.text" rows={2} />
                    </div>
                    <div>
                        <h5 className="font-semibold text-sm text-gray-600 mb-2">Impact</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.benefits.items.impact.title" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.benefits.items.impact.text" rows={2} />
                    </div>
                    <div>
                        <h5 className="font-semibold text-sm text-gray-600 mb-2">Culture</h5>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.benefits.items.culture.title" />
                        <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.benefits.items.culture.text" rows={2} />
                    </div>
                </div>
              </SectionCard>

              {/* Application Form Labels */}
              <SectionCard title="Application Form Labels">
                 <div className="grid grid-cols-3 gap-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="First Name" path="careerPage.applicationForm.firstName" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Last Name" path="careerPage.applicationForm.lastName" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Date of Birth" path="careerPage.applicationForm.dateOfBirth" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Phone" path="careerPage.applicationForm.phoneNumber" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Email" path="careerPage.applicationForm.email" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Gender" path="careerPage.applicationForm.gender" />
                 </div>
                 <div className="mt-4">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="File Upload Label" path="careerPage.applicationForm.fileUpload" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Upload Button" path="careerPage.applicationForm.fileUploadButton" />
                 </div>
              </SectionCard>


              
              {/* CTA Section */}
              <SectionCard title="Bottom CTA">
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="careerPage.cta.title" />
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Text" path="careerPage.cta.text" />
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Button Text" path="careerPage.cta.button" />
              </SectionCard>
            </>
          )}

          {activeTab === 'footer' && (
            <>
              <SectionCard title="Footer Content">
                {/* Description */}
                <div className="mb-6">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Company Description" path="footer.description" rows={3} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Navigation Column */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Navigation Column</h4>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Header Title" path="footer.navigation" />
                        <div className="mt-4 space-y-3">
                            <label className="block text-sm font-semibold text-gray-500">Menu Items (Global)</label>
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Home Label" path="nav.home" />
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="About Label" path="nav.about" />
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Product Label" path="nav.service" />
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Career Label" path="nav.career" />
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Contact Label" path="nav.contact" />
                        </div>
                    </div>

                    {/* Contact Column */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Contact Column</h4>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Header Title" path="footer.contact" />
                        <div className="mt-4 space-y-3">
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Address" path="footer.contactInfo.location" />
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Email" path="footer.contactInfo.email" />
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Phone" path="footer.contactInfo.phone" />
                        </div>
                    </div>
                </div>

                {/* Footer Bottom */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                    <h4 className="font-bold text-gray-700 mb-4">Bottom Bar</h4>
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Copyright Text" path="footer.copyright" />
                </div>
              </SectionCard>
            </>
          )}

          {activeTab === 'contact' && (
            <>
              {/* Hero Section */}
              <SectionCard title="Hero Section">
                <div className="grid grid-cols-2 gap-6">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Badge" path="contactPage.hero.badge" />
                </div>
                <div className="mt-4">
                   <ImageField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Background Image URL" path="contactPage.hero.image" />
                </div>
                <div className="grid grid-cols-2 gap-6 mt-4">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title Line 1" path="contactPage.hero.titleLine1" />
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Emphasis Word" path="contactPage.hero.titleEmphasis" />
                </div>
                <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Subtitle" path="contactPage.hero.subtitle" />
              </SectionCard>

              {/* Contact Cards */}
              <SectionCard title="Contact Cards">
                 <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Visit Us</h4>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="contactPage.cards.visitTitle" />
                    <div className="space-y-2">
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Address Line 1" path="contactPage.cards.visitLine1" />
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Address Line 2" path="contactPage.cards.visitLine2" />
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Address Line 3" path="contactPage.cards.visitLine3" />
                    </div>
                 </div>

                 <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Call Us</h4>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="contactPage.cards.callTitle" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Main Office Label" path="contactPage.cards.callMainLabel" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Sales Dept Label" path="contactPage.cards.callSalesLabel" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Main Office Number" path="contactPage.cards.callMainNumber" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Sales Dept Number" path="contactPage.cards.callSalesNumber" />
                 </div>

                 <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Email Us</h4>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="contactPage.cards.emailTitle" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="General Email Label" path="contactPage.cards.emailGeneralLabel" />
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Sales Email Label" path="contactPage.cards.emailSalesLabel" />
                 </div>
              </SectionCard>

              {/* Form Labels */}
              <SectionCard title="Contact Form Labels">
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Form Title" path="contactPage.form.title" />
                  <div className="grid grid-cols-2 gap-6 mt-4">
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Full Name Label" path="contactPage.form.fullName" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Company Name Label" path="contactPage.form.companyName" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Industry Label" path="contactPage.form.industry" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Message Label" path="contactPage.form.message" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Submit Button" path="contactPage.form.submit" />
                  </div>
              </SectionCard>

              {/* Sidebar Info */}
              <SectionCard title="Sidebar Information">
                  <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Business Hours</h4>
                  <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Title" path="contactPage.sidebar.businessHoursTitle" />
                  <div className="grid grid-cols-2 gap-6 mt-4">
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Sun-Thu Label" path="contactPage.sidebar.sundayThursday" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Fri Label" path="contactPage.sidebar.friday" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Sat Label" path="contactPage.sidebar.saturday" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Closed Label" path="contactPage.sidebar.closed" />
                  </div>
                  
                  <div className="mt-6">
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Connect Title" path="contactPage.sidebar.connectTitle" />
                      <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Certified Title" path="contactPage.sidebar.certifiedTitle" />
                  </div>
              </SectionCard>


              {/* Messages */}
              <SectionCard title="Messages">
                <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Success Toast" path="contactPage.toast.success" />
              </SectionCard>
            </>
          )}

          {activeTab === 'footer' && (
            <>
              <SectionCard title="Footer Content">
                {/* Description */}
                <div className="mb-6">
                    <TextAreaField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Company Description" path="footer.description" rows={3} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Navigation Column */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Navigation Column</h4>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Header Title" path="footer.navigation" />
                        <div className="mt-4 space-y-3">
                            <label className="block text-sm font-semibold text-gray-500">Menu Items (Footer)</label>
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Home Label" path="footer.navLinks.home" />
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="About Label" path="footer.navLinks.about" />
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Product Label" path="footer.navLinks.service" />
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Career Label" path="footer.navLinks.career" />
                            <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Contact Label" path="footer.navLinks.contact" />
                        </div>
                    </div>

                    {/* Contact Column */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Contact Column</h4>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Header Title" path="footer.contact" />
                        <div className="mt-4 space-y-3">
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Address" path="footer.contactInfo.location" />
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Email" path="footer.contactInfo.email" />
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Phone" path="footer.contactInfo.phone" />
                        </div>
                    </div>

                    {/* Catalog/Industry Column */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 mt-6 md:col-span-2">
                        <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">Catalog Column</h4>
                        <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Header Title" path="footer.industries" />
                        <div className="mt-4 grid grid-cols-2 gap-4">
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Oil & Gas Label" path="footer.industryLinks.oil" />
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Petro Label" path="footer.industryLinks.petro" />
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Power Label" path="footer.industryLinks.power" />
                             <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Infrastructure Label" path="footer.industryLinks.infrastructure" />
                        </div>
                    </div>
                </div>

                {/* Footer Bottom */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                    <h4 className="font-bold text-gray-700 mb-4">Bottom Bar</h4>
                    <InputField getValue={getValue} updateField={updateField} lang={selectedLanguage} label="Copyright Text" path="footer.copyright" />
                </div>
              </SectionCard>
            </>
          )}


        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
